/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * Ortak AI composer satırı. Ürün içi global chat ve AI asistan ekranı aynı
 * input/menu/mikrofon/gönder kabuğunu bu bileşenle kullanır.
 */
import React from 'react';
import { ArrowUp, Mic, Square } from 'lucide-react';
import { AssistantComposerMenu, type AssistantComposerMenuItem } from './AssistantComposerMenu';
import './AssistantComposerBar.css';

export type { AssistantComposerMenuItem } from './AssistantComposerMenu';

export type AssistantComposerMention = {
  id: number | string;
  name: string;
  start: number;
  end: number;
};

export type AssistantExpertPrompt = {
  assistantId: number | string;
  assistantName: string;
  question: string;
};

export function insertAssistantComposerMention(
  value: string,
  mentions: AssistantComposerMention[],
  assistant: { id: number | string; name: string },
): { value: string; mentions: AssistantComposerMention[] } {
  const match = /(^|\s)@[^@\n]*$/u.exec(value);
  const token = `@${assistant.name}`;
  const tokenStart = match
    ? match.index + match[1].length
    : value.length + (value && !value.endsWith(' ') ? 1 : 0);
  const prefix = match
    ? value.slice(0, tokenStart)
    : `${value}${value && !value.endsWith(' ') ? ' ' : ''}`;
  const nextValue = `${prefix}${token} `;
  const retainedMentions = normalizedComposerMentions(value, mentions)
    .filter((mention) => mention.end <= tokenStart);
  return {
    value: nextValue,
    mentions: [
      ...retainedMentions,
      {
        id: assistant.id,
        name: assistant.name,
        start: prefix.length,
        end: prefix.length + token.length,
      },
    ],
  };
}

export function buildAssistantExpertPrompts(
  value: string,
  mentions: AssistantComposerMention[],
): AssistantExpertPrompt[] {
  const trustedMentions = normalizedComposerMentions(value, mentions);
  if (trustedMentions.length === 0) return [];
  const preamble = value.slice(0, trustedMentions[0].start).trim();
  const grouped = new Map<string, AssistantExpertPrompt>();

  trustedMentions.forEach((mention, index) => {
    const nextStart = trustedMentions[index + 1]?.start ?? value.length;
    const segment = value.slice(mention.end, nextStart).trim();
    const question = [preamble, segment].filter(Boolean).join('\n').trim();
    const key = String(mention.id);
    const current = grouped.get(key);
    if (current) {
      current.question = [current.question, segment].filter(Boolean).join('\n').trim();
      return;
    }
    grouped.set(key, {
      assistantId: mention.id,
      assistantName: mention.name,
      question,
    });
  });

  return Array.from(grouped.values());
}

interface AssistantComposerBarClasses {
  row?: string;
  stacked?: string;
  inputRow?: string;
  actionsRow?: string;
  secondaryActions?: string;
  menuWrapper?: string;
  menuButton?: string;
  menuButtonActive?: string;
  menuPanel?: string;
  menuItem?: string;
  inputShell?: string;
  input?: string;
  micButton?: string;
  micActive?: string;
  sendButton?: string;
}

interface AssistantComposerBarProps {
  menuOpen: boolean;
  onMenuOpenChange: (open: boolean) => void;
  menuItems: AssistantComposerMenuItem[];
  menuLabel: string;
  menuDisabled?: boolean;
  inputRef?: React.Ref<HTMLInputElement> | React.Ref<HTMLTextAreaElement> | React.Ref<HTMLSpanElement>;
  value: string;
  placeholder: string;
  disabled?: boolean;
  onValueChange: (value: string) => void;
  onSubmit: () => void;
  isListening: boolean;
  micTitle: string;
  micAriaLabel: string;
  onMicToggle: () => void;
  sendDisabled: boolean;
  sendTitle: string;
  sendAriaLabel?: string;
  isProcessing?: boolean;
  stopTitle?: string;
  stopAriaLabel?: string;
  onStop?: () => void;
  layout?: 'inline' | 'stacked';
  /** Stacked form mode keeps the action row while its own form owns text input. */
  hideInput?: boolean;
  /** Stacked düzende işlem satırının ölçüm ve menü yerleşim sınırı referansı. */
  actionsRowRef?: React.Ref<HTMLDivElement>;
  secondaryActions?: React.ReactNode;
  /** Input içinde güvenilir `@Uzman` etiketi gibi kanonik bir önek. */
  leadingToken?: React.ReactNode;
  /** İmleç metnin başındayken Backspace ile önek etiketini tek parça kaldırır. */
  onLeadingTokenRemove?: () => void;
  /** İki satırlı, satır kaydıran kurumsal sohbet editörü. */
  multiline?: boolean;
  /** Güvenilir uzman etiketini metinle aynı satır akışında tutan zengin editör. */
  inlineEditor?: boolean;
  /** Editör metni içinde picker ile doğrulanmış, atomik `@Uzman` referansları. */
  mentions?: AssistantComposerMention[];
  /** Yazma/silme sonrası güvenilir referansların güncel metin konumlarını bildirir. */
  onMentionsChange?: (mentions: AssistantComposerMention[]) => void;
  /** Mikrofonun hemen solunda gösterilen çalışma modu seçicisi. */
  workModeControl?: React.ReactNode;
  classes?: AssistantComposerBarClasses;
}

type AssistantComposerInputElement = HTMLInputElement | HTMLTextAreaElement | HTMLSpanElement;

type SerializedComposerEditor = {
  value: string;
  mentions: AssistantComposerMention[];
  mentionNodes: Array<{ mention: AssistantComposerMention; node: HTMLElement }>;
};

function mentionSignature(mentions: AssistantComposerMention[]): string {
  return mentions
    .map((mention) => `${String(mention.id)}:${mention.name}:${mention.start}:${mention.end}`)
    .join('|');
}

function normalizedComposerMentions(
  value: string,
  mentions: AssistantComposerMention[],
): AssistantComposerMention[] {
  let previousEnd = 0;
  return [...mentions]
    .sort((left, right) => left.start - right.start || left.end - right.end)
    .filter((mention) => {
      const token = `@${mention.name}`;
      const valid = mention.start >= previousEnd
        && mention.end === mention.start + token.length
        && value.slice(mention.start, mention.end) === token;
      if (valid) previousEnd = mention.end;
      return valid;
    });
}

function readComposerEditor(root: HTMLElement): SerializedComposerEditor {
  let value = '';
  const mentions: AssistantComposerMention[] = [];
  const mentionNodes: SerializedComposerEditor['mentionNodes'] = [];

  const visit = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      value += node.textContent || '';
      return;
    }
    if (!(node instanceof HTMLElement)) return;
    if (node.matches('[data-assistant-mention-id]')) {
      const name = node.dataset.assistantMentionName || '';
      if (!name) return;
      const start = value.length;
      value += `@${name}`;
      const mention: AssistantComposerMention = {
        id: node.dataset.assistantMentionId || '',
        name,
        start,
        end: value.length,
      };
      mentions.push(mention);
      mentionNodes.push({ mention, node });
      return;
    }
    if (node.tagName === 'BR') {
      value += '\n';
      return;
    }

    const block = node.tagName === 'DIV' || node.tagName === 'P';
    if (block && value && !value.endsWith('\n')) value += '\n';
    node.childNodes.forEach(visit);
  };

  root.childNodes.forEach(visit);
  return { value, mentions, mentionNodes };
}

function renderComposerEditor(
  root: HTMLElement,
  value: string,
  mentions: AssistantComposerMention[],
): void {
  const fragment = document.createDocumentFragment();
  let cursor = 0;
  normalizedComposerMentions(value, mentions).forEach((mention) => {
    if (mention.start > cursor) {
      fragment.append(document.createTextNode(value.slice(cursor, mention.start)));
    }
    const token = document.createElement('span');
    token.className = 'assistant-inline-mention';
    token.contentEditable = 'false';
    token.dataset.assistantMentionId = String(mention.id);
    token.dataset.assistantMentionName = mention.name;
    token.setAttribute('aria-label', `@${mention.name}`);
    token.textContent = `@${mention.name}`;
    fragment.append(token);
    cursor = mention.end;
  });
  if (cursor < value.length) fragment.append(document.createTextNode(value.slice(cursor)));
  root.replaceChildren(fragment);
}

function composerCaretOffset(root: HTMLElement): number | null {
  const selection = window.getSelection();
  if (!selection?.isCollapsed || selection.rangeCount === 0) return null;
  const range = selection.getRangeAt(0);
  if (!root.contains(range.startContainer)) return null;
  const beforeCaret = range.cloneRange();
  beforeCaret.selectNodeContents(root);
  beforeCaret.setEnd(range.startContainer, range.startOffset);
  return beforeCaret.toString().length;
}

function placeComposerCaret(root: HTMLElement, offset: number): void {
  const selection = window.getSelection();
  const range = document.createRange();
  let remaining = Math.max(0, offset);
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let textNode = walker.nextNode();
  while (textNode) {
    const length = textNode.textContent?.length || 0;
    if (remaining <= length) {
      range.setStart(textNode, remaining);
      range.collapse(true);
      selection?.removeAllRanges();
      selection?.addRange(range);
      return;
    }
    remaining -= length;
    textNode = walker.nextNode();
  }
  range.selectNodeContents(root);
  range.collapse(false);
  selection?.removeAllRanges();
  selection?.addRange(range);
}

function assignComposerInputRef(
  ref: AssistantComposerBarProps['inputRef'],
  node: AssistantComposerInputElement | null,
): void {
  if (typeof ref === 'function') {
    (ref as React.RefCallback<AssistantComposerInputElement>)(node);
    return;
  }
  if (ref) {
    (ref as React.MutableRefObject<AssistantComposerInputElement | null>).current = node;
  }
}

function joinClasses(...values: Array<string | false | null | undefined>): string {
  return values.filter(Boolean).join(' ');
}

export function AssistantComposerBar({
  menuOpen,
  onMenuOpenChange,
  menuItems,
  menuLabel,
  menuDisabled = false,
  inputRef,
  value,
  placeholder,
  disabled = false,
  onValueChange,
  onSubmit,
  isListening,
  micTitle,
  micAriaLabel,
  onMicToggle,
  sendDisabled,
  sendTitle,
  sendAriaLabel,
  isProcessing = false,
  stopTitle = 'Stop response',
  stopAriaLabel,
  onStop,
  layout = 'inline',
  hideInput = false,
  actionsRowRef,
  secondaryActions,
  leadingToken,
  onLeadingTokenRemove,
  multiline = false,
  inlineEditor = false,
  mentions = [],
  onMentionsChange,
  workModeControl,
  classes,
}: AssistantComposerBarProps) {
  const inlineEditorRef = React.useRef<HTMLSpanElement | null>(null);
  const setInlineEditorRef = React.useCallback((node: HTMLSpanElement | null) => {
    inlineEditorRef.current = node;
    assignComposerInputRef(inputRef, node);
  }, [inputRef]);

  React.useLayoutEffect(() => {
    if (!inlineEditor) return;
    const editorElement = inlineEditorRef.current;
    if (!editorElement) return;

    const current = readComposerEditor(editorElement);
    const normalizedMentions = normalizedComposerMentions(value, mentions);
    if (
      current.value === value
      && mentionSignature(current.mentions) === mentionSignature(normalizedMentions)
    ) return;

    renderComposerEditor(editorElement, value, normalizedMentions);
    if (document.activeElement !== editorElement) return;

    const selection = window.getSelection();
    const range = document.createRange();
    range.selectNodeContents(editorElement);
    range.collapse(false);
    selection?.removeAllRanges();
    selection?.addRange(range);
  }, [inlineEditor, mentions, value]);

  const emitInlineEditorState = React.useCallback((editorElement: HTMLSpanElement) => {
    const next = readComposerEditor(editorElement);
    onValueChange(next.value);
    onMentionsChange?.(next.mentions);
  }, [onMentionsChange, onValueChange]);

  const handleInputShellMouseDown = React.useCallback((
    event: React.MouseEvent<HTMLDivElement>,
  ) => {
    if (!inlineEditor || disabled) return;
    const editorElement = inlineEditorRef.current;
    if (!editorElement || !(event.target instanceof Node)) return;
    if (editorElement.contains(event.target)) return;
    if (
      event.target instanceof Element
      && event.target.closest('button, input, textarea, select, a, [role="button"], .assistant-target-chip')
    ) return;

    event.preventDefault();
    editorElement.focus();
    placeComposerCaret(editorElement, readComposerEditor(editorElement).value.length);
  }, [disabled, inlineEditor]);

  const menu = (
    <AssistantComposerMenu
      open={menuOpen}
      onOpenChange={onMenuOpenChange}
      items={menuItems}
      label={menuLabel}
      disabled={menuDisabled}
      wrapperClassName={classes?.menuWrapper}
      buttonClassName={classes?.menuButton}
      activeButtonClassName={classes?.menuButtonActive}
      menuClassName={classes?.menuPanel}
      itemClassName={classes?.menuItem}
    />
  );

  const micButton = (
    <button
      type="button"
      className={joinClasses(classes?.micButton, isListening && classes?.micActive)}
      onClick={onMicToggle}
      title={micTitle}
      aria-label={micAriaLabel}
      disabled={disabled}
    >
      <Mic size={17} aria-hidden />
    </button>
  );

  const stopMode = isProcessing && Boolean(onStop);
  const submitState = stopMode ? 'stop' : sendDisabled ? 'idle' : 'ready';
  const submitTitle = stopMode ? stopTitle : sendTitle;

  const sendButton = (
    <button
      type="button"
      className={classes?.sendButton}
      data-state={submitState}
      onClick={stopMode ? onStop : onSubmit}
      disabled={stopMode ? false : sendDisabled}
      title={submitTitle}
      aria-label={stopMode ? (stopAriaLabel || stopTitle) : (sendAriaLabel || sendTitle)}
    >
      {stopMode
        ? <Square size={11} fill="currentColor" aria-hidden />
        : <ArrowUp size={18} strokeWidth={2.4} aria-hidden />}
    </button>
  );

  const isCaretAtStart = (
    target: HTMLInputElement | HTMLTextAreaElement | HTMLSpanElement,
  ): boolean => {
    if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) {
      return target.selectionStart === 0 && target.selectionEnd === 0;
    }
    const selection = window.getSelection();
    if (!selection?.isCollapsed || selection.rangeCount === 0) return false;
    const range = selection.getRangeAt(0);
    if (!target.contains(range.startContainer)) return false;
    const contentBeforeCaret = range.cloneRange();
    contentBeforeCaret.selectNodeContents(target);
    contentBeforeCaret.setEnd(range.startContainer, range.startOffset);
    return contentBeforeCaret.toString().length === 0;
  };

  const handleInputKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSpanElement>,
  ) => {
    if (event.defaultPrevented) return;
    if (
      event.key === 'Backspace'
      && leadingToken
      && onLeadingTokenRemove
      && isCaretAtStart(event.currentTarget)
    ) {
      event.preventDefault();
      onLeadingTokenRemove();
      return;
    }
    if (
      inlineEditor
      && (event.key === 'Backspace' || event.key === 'Delete')
      && event.currentTarget instanceof HTMLSpanElement
    ) {
      const caret = composerCaretOffset(event.currentTarget);
      const serialized = readComposerEditor(event.currentTarget);
      const adjacent = serialized.mentionNodes.find(({ mention }) =>
        event.key === 'Backspace' ? mention.end === caret : mention.start === caret);
      if (adjacent && caret != null) {
        event.preventDefault();
        const nextCaret = event.key === 'Backspace' ? adjacent.mention.start : caret;
        adjacent.node.remove();
        emitInlineEditorState(event.currentTarget);
        window.setTimeout(() => placeComposerCaret(event.currentTarget, nextCaret), 0);
        return;
      }
    }
    if (
      event.key === 'Enter'
      && (!multiline || !event.shiftKey)
      && !event.nativeEvent.isComposing
    ) {
      event.preventDefault();
      onSubmit();
    }
  };

  const editor = inlineEditor ? (
    <span
      ref={setInlineEditorRef}
      role="textbox"
      contentEditable={disabled ? false : 'plaintext-only'}
      suppressContentEditableWarning
      spellCheck
      className={classes?.input}
      data-placeholder={placeholder}
      aria-label={placeholder}
      aria-placeholder={placeholder}
      aria-multiline={multiline}
      onInput={(event) => emitInlineEditorState(event.currentTarget)}
      onKeyDown={handleInputKeyDown}
      data-disabled={disabled ? 'true' : undefined}
    />
  ) : multiline ? (
    <textarea
      ref={inputRef as React.Ref<HTMLTextAreaElement>}
      rows={2}
      className={classes?.input}
      placeholder={placeholder}
      value={value}
      onChange={(event) => onValueChange(event.target.value)}
      onKeyDown={handleInputKeyDown}
      disabled={disabled}
    />
  ) : (
    <input
      ref={inputRef as React.Ref<HTMLInputElement>}
      type="text"
      className={classes?.input}
      placeholder={placeholder}
      value={value}
      onChange={(event) => onValueChange(event.target.value)}
      onKeyDown={handleInputKeyDown}
      disabled={disabled}
    />
  );

  const input = (
    <div
      className={joinClasses('assistant-composer-input-shell', classes?.inputShell)}
      data-disabled={disabled ? 'true' : undefined}
      onMouseDown={handleInputShellMouseDown}
    >
      {leadingToken}
      {editor}
      {layout === 'inline' ? workModeControl : null}
      {layout === 'inline' ? micButton : null}
      {layout === 'inline' ? sendButton : null}
    </div>
  );

  if (layout === 'stacked') {
    return (
      <div className={joinClasses(classes?.row, classes?.stacked)}>
        {!hideInput && <div className={classes?.inputRow}>{input}</div>}
        <div className={classes?.actionsRow} ref={actionsRowRef}>
          {menu}
          {secondaryActions ? <div className={classes?.secondaryActions}>{secondaryActions}</div> : null}
          {workModeControl}
          {micButton}
          {sendButton}
        </div>
      </div>
    );
  }

  return (
    <div className={classes?.row}>
      {menu}
      {input}
    </div>
  );
}

export default AssistantComposerBar;
